/**
 * @graphql cc
 */
import React, { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents, type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SmartCardProvider } from '@atlaskit/link-provider';

import { getCorrectContentURL } from '@atlassian/confluence-page-preview-panel';

import { expValEquals } from '@confluence/feature-experiments';
import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import { usePageSpaceKey } from '@confluence/page-context';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { useIsOnRoute } from '@confluence/route-manager/entry-points/RouteState';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { PreviewPanelsPostMessageListener } from '@confluence/preview-panel-api/entry-points/PreviewPanelsPostMessageListener';
import { useOpenPreviewPanel } from '@confluence/preview-panel-api/entry-points/useOpenPreviewPanel';
import { isPreviewPanelAvailable } from '@confluence/preview-panel-api/entry-points/isPreviewPanelAvailable';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import {
	type FragmentReaderInterop,
	useReadFragmentInterop,
} from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop';
import {
	useWriteFragmentInterop,
	type FragmentWriterInterop,
} from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';
import type { ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment$data } from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment.graphql';
import type { ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment$data } from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment.graphql';
import type { ConfluenceSmartCardProviderCurrentSmartCardProviderReadFragment$data } from './__generated__/ConfluenceSmartCardProviderCurrentSmartCardProviderReadFragment.graphql';
import type { ConfluenceSmartCardProviderCurrentSmartCardProviderWriteFragment$data } from './__generated__/ConfluenceSmartCardProviderCurrentSmartCardProviderWriteFragment.graphql';
import { ConfluenceSmartCardClient } from './ConfluenceSmartCardClient';
import { liveDocSmartCardResolver } from './resolvers/liveDocSmartCardResolver';

export type ConfluenceSmartCardProviderProps = Parameters<typeof SmartCardProvider>[0] & {
	doNotUsePreviewPanels?: boolean;
};

// Only log first error to console, to prevent spamming console with resolver errors
let hasLoggedResolverError = false;
const defaultConfluenceResolveHandler = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	readFragmentInterop: FragmentReaderInterop<any>,
	writeFragmentInterop: FragmentWriterInterop<any>,
	data?: unknown,
) => {
	try {
		// NOTE FOR DEVS OF RESOLVERS: The `data` object comes from linking platform's content resolver service which is an UNSTABLE API SCHEMA.
		// Ensure your resolver logic handles invalid schemas gracefully and is able to notify your team if the schema changes and your code needs to be updated.
		liveDocSmartCardResolver(createAnalyticsEvent, readFragmentInterop, writeFragmentInterop, data);
	} catch (resolverError) {
		// prevent errors from causing smart card provider issues
		if (!hasLoggedResolverError) {
			const logger = getLogger('ConfluenceSmartCardProvider');
			logger.error`resolver error: ${resolverError}`;
			hasLoggedResolverError = true;
		}
	}
};

const CurrentSmartCardProvider: typeof SmartCardProvider = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const readFragmentInterop =
		useReadFragmentInterop<ConfluenceSmartCardProviderCurrentSmartCardProviderReadFragment$data>({
			fragmentSpec: graphql`
				fragment ConfluenceSmartCardProviderCurrentSmartCardProviderReadFragment on Content {
					id
					subType
					status
					__typename
				}
			`,
		});
	const writeFragmentInterop =
		useWriteFragmentInterop<ConfluenceSmartCardProviderCurrentSmartCardProviderWriteFragment$data>({
			fragmentSpec: graphql`
				fragment ConfluenceSmartCardProviderCurrentSmartCardProviderWriteFragment on Content {
					id
					subType
					status
					__typename
				}
			`,
		});
	const smartCardClient = useMemo(
		() =>
			props?.client ||
			new ConfluenceSmartCardClient({
				onResolve: (data) =>
					defaultConfluenceResolveHandler(
						createAnalyticsEvent,
						readFragmentInterop,
						writeFragmentInterop,
						data,
					),
			}),
		[props?.client, createAnalyticsEvent, readFragmentInterop, writeFragmentInterop],
	);

	return <SmartCardProvider product="CONFLUENCE" client={smartCardClient} {...props} />;
};

const ConsolidatedSmartCardProvider: typeof SmartCardProvider = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const readFragmentInterop =
		useReadFragmentInterop<ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment$data>(
			{
				fragmentSpec: graphql`
					fragment ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment on Content {
						id
						subType
						status
						__typename
					}
				`,
			},
		);
	const writeFragmentInterop =
		useWriteFragmentInterop<ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment$data>(
			{
				fragmentSpec: graphql`
					fragment ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment on Content {
						id
						subType
						status
						__typename
					}
				`,
			},
		);
	const smartCardClient = useMemo(
		() =>
			props?.client ||
			new ConfluenceSmartCardClient({
				onResolve: (data) =>
					defaultConfluenceResolveHandler(
						createAnalyticsEvent,
						readFragmentInterop,
						writeFragmentInterop,
						data,
					),
			}),
		[props?.client, createAnalyticsEvent, readFragmentInterop, writeFragmentInterop],
	);

	const { isAdminHubAIEnabled } = useSessionData();

	const isPageSSR = isPageSSRd();
	const initialState = window['__SSR_SMARTLINKS__'];
	const internalStoreOptions = useStoreOptions(initialState, isPageSSR);

	// Merge smartlink SSR data with any passed-in storeOptions (e.g. from shortcuts preloader)
	const presetInitialState = {
		...internalStoreOptions,
		...(props.storeOptions
			? {
					storeOptions: {
						...(internalStoreOptions.storeOptions || {}),
						...props.storeOptions,
					},
				}
			: {}),
	};

	const shouldControlDataExport = useIsOnRoute(PDF_EXPORT_PAGE);

	return (
		<SmartCardProvider
			product="CONFLUENCE"
			client={smartCardClient}
			isAdminHubAIEnabled={isAdminHubAIEnabled}
			shouldControlDataExport={shouldControlDataExport}
			isPreviewPanelAvailable={props.isPreviewPanelAvailable}
			openPreviewPanel={props.openPreviewPanel}
			{...presetInitialState}
			{...props}
		/>
	);
};

const ConsolidatedPreviewPanelSmartCardProvider = (props: ConfluenceSmartCardProviderProps) => {
	const { open } = useOpenPreviewPanel();
	const [spaceKey] = usePageSpaceKey();
	const { cloudId } = useSessionData();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	// Create a wrapper function that updates the preview panel URL
	// This is used for custom domains to update to the correct content URL and avoid CSP issues
	const openPreviewPanelWithModifiedURL = useMemo(() => {
		if (!cloudId) {
			return open;
		}
		return ({ url, ari, ...rest }: Parameters<typeof open>[0]) =>
			open({ url: getCorrectContentURL(cloudId, url, ari), ari, ...rest });
	}, [open, cloudId]);

	const { children, doNotUsePreviewPanels, ...restProps } = props;

	return isCompanyHubSpaceKey(spaceKey) || doNotUsePreviewPanels ? (
		<ConsolidatedSmartCardProvider {...restProps}>{children}</ConsolidatedSmartCardProvider>
	) : (
		<ConsolidatedSmartCardProvider
			openPreviewPanel={openPreviewPanelWithModifiedURL}
			isPreviewPanelAvailable={
				renderServerPlaceholder && fg('confluence_fix_smartlink_hydration_error')
					? (_ari) => false
					: isPreviewPanelAvailable
			}
			{...restProps}
		>
			{children}
			<PreviewPanelsPostMessageListener />
		</ConsolidatedSmartCardProvider>
	);
};

export const ConfluenceSmartCardProvider = (props: ConfluenceSmartCardProviderProps) => {
	const isNav4Enabled = useIsNav4Enabled();

	if (expValEquals('confluence_preview_panels_exp', 'isEnabled', true)) {
		return isNav4Enabled && expValEquals('confluence_preview_panels_exp', 'isEnabled', true) ? (
			<ConsolidatedPreviewPanelSmartCardProvider {...props} />
		) : (
			<ConsolidatedSmartCardProvider {...props} />
		);
	} else {
		return <CurrentSmartCardProvider {...props} />;
	}
};
